import {
  type Locale,
  format,
  formatDuration,
  intervalToDuration,
} from 'date-fns';

import {
  extractLastNameComponentFromName,
  formatDatePast,
} from '@spotify/confidence-core-react';
import {
  type RecordingRefFragment,
  RecordingV1AnalysisLevel,
} from '@spotify/confidence-graphql';

import { DISPLAY_NAMES } from './constants';
import {
  RecordingMetricKey,
  getMetric,
  isUserInteractionMetric,
} from './metrics';

export const formatPlaybackTime = (ms: number): string => {
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  const ss = String(seconds).padStart(2, '0');

  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
};

export const parseDurationSeconds = (duration: string): number | undefined => {
  const match = duration.match(/^(\d+(?:\.\d+)?)s$/);
  return match ? Number(match[1]) : undefined;
};

export type RecordingOffset = {
  label: string;
  seconds: number;
};

export const getRecordingOffset = (
  recordingOffset?: string | null,
): RecordingOffset | undefined => {
  if (!recordingOffset) return undefined;

  const seconds = parseDurationSeconds(recordingOffset);
  return seconds === undefined
    ? undefined
    : { label: formatPlaybackTime(seconds * 1000), seconds };
};

export const parseTimestamp = (input: string): number | undefined => {
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  const parts = trimmed.split(':');

  if (parts.length === 1) {
    const seconds = Number(parts[0]);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
  }

  if (parts.length === 2) {
    const [m, s] = parts.map(Number);
    if (!Number.isFinite(m) || !Number.isFinite(s)) return undefined;
    if (m < 0 || s < 0 || s >= 60) return undefined;
    return m * 60 + s;
  }

  if (parts.length === 3) {
    const [h, m, s] = parts.map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s))
      return undefined;
    if (h < 0 || m < 0 || m >= 60 || s < 0 || s >= 60) return undefined;
    return h * 3600 + m * 60 + s;
  }

  return undefined;
};

type RecordingRow = {
  name: string;
  displayName: string;
  aiAnalyzed: boolean;
  time: string;
  duration: string;
  os: string;
  browser: string;
  pagesVisited: string[];
  pageCount: number;
  eventCount: number;
};

export const isRecordingAiAnalyzed = (
  analysisLevel: RecordingRefFragment['analysisLevel'],
): boolean => analysisLevel !== RecordingV1AnalysisLevel.AnalysisLevelDisabled;

export const formatLabel = (value?: string, type?: string): string => {
  if (!value) return `Unknown ${type ?? ''}`;

  return DISPLAY_NAMES[value.toLowerCase()] ?? value;
};

export const formatFlagVariants = (
  variants: ReadonlyArray<{ variant: string }>,
): string =>
  [
    ...new Set(
      variants.map(({ variant }) => extractLastNameComponentFromName(variant)),
    ),
  ].join(', ');

export const formatTagValues = (
  values: readonly string[] | null | undefined,
): string => [...new Set(values ?? [])].join(', ');

export const formatRecordingTime = (dateString: string): string => {
  const date = new Date(dateString);
  const time = format(date, 'HH:mm');

  return `${formatDatePast(date)}, ${time}`;
};

const SHORT_DURATION_UNITS: Record<string, string> = {
  xHours: 'h',
  xMinutes: 'm',
  xSeconds: 's',
};

const shortDurationLocale: Locale = {
  formatDistance: (token, count) =>
    `${count}${SHORT_DURATION_UNITS[token] ?? ''}`,
} as Locale;

export const formatShortDuration = (seconds: number): string => {
  if (seconds === 0) return '0s';

  return formatDuration(intervalToDuration({ start: 0, end: seconds * 1000 }), {
    format: ['hours', 'minutes', 'seconds'],
    locale: shortDurationLocale,
  });
};

export const formatRecordingDuration = (
  duration: string | undefined,
): string => {
  if (!duration) return '-';

  const seconds = parseDurationSeconds(duration);

  if (seconds === undefined || seconds < 0) return '-';
  if (seconds < 1 && seconds > 0) return '< 1s';

  return formatShortDuration(seconds);
};

export const toRow = (recording: RecordingRefFragment): RecordingRow => {
  const eventCount = Object.values(RecordingMetricKey)
    .filter(isUserInteractionMetric)
    .reduce(
      (sum, metricKey) => sum + (getMetric(recording.metrics, metricKey) ?? 0),
      0,
    );

  return {
    name: recording.name,
    displayName: recording.displayName ?? 'Unknown',
    aiAnalyzed: isRecordingAiAnalyzed(recording.analysisLevel),
    time: formatRecordingTime(recording.createTime),
    duration: formatRecordingDuration(recording.duration ?? undefined),
    os: recording.environmentInfo?.os ?? '',
    browser: recording.environmentInfo?.browser ?? '',
    pagesVisited: (recording.routing?.routes ?? []).map(p => p.pageUri),
    pageCount: recording.routing?.routes?.length ?? 0,
    eventCount,
  };
};
